import { VIETNAM_BANKS, FONT_SIZES, DEFAULT_PRINT_SETTINGS } from '../constants';
import { formatCurrency, generateOrderCode, getTotalItemsCount } from './formatters';

export const getPrintSettings = () => {
  const saved = localStorage.getItem('printSettings');
  return saved ? JSON.parse(saved) : DEFAULT_PRINT_SETTINGS;
};

export const generateQRCodeUrl = (order, defaultBank, printSettings, orderCode) => {
  if (!printSettings.showQRCode || !defaultBank) return '';
  
  const bank = VIETNAM_BANKS.find(b => b.code === defaultBank.bankCode);
  if (!bank) return '';
  
  const transferPrefix = printSettings.transferPrefix || '';
  const transferContent = transferPrefix ? `${transferPrefix} ${orderCode}` : orderCode;
  
  return `https://img.vietqr.io/image/${bank.bin}-${defaultBank.accountNumber}-compact.png?amount=${order.totalAmount}&addInfo=${encodeURIComponent(transferContent)}`;
};

export const generatePrintHTML = (order, shopSettings, printSettings = null) => {
  const settings = printSettings || getPrintSettings();
  const fs = FONT_SIZES[settings.fontSize] || FONT_SIZES.normal;
  
  const defaultBank = shopSettings?.bankAccounts?.find(b => b.isDefault);
  const orderCode = generateOrderCode(order);
  
  // Generate transfer content
  const transferPrefix = settings.transferPrefix || '';
  const transferContent = transferPrefix ? `${transferPrefix} ${orderCode}` : orderCode;
  
  // Generate QR code URL
  const qrCodeUrl = generateQRCodeUrl(order, defaultBank, settings, orderCode);
  
  // Customer name display
  const customerName = order.customer?.name || order.customerName || 
    (order.table?.name || (order.tableNumber ? 'Bàn ' + order.tableNumber : 'Mang đi'));
  
  // Generate items HTML
  const itemsHTML = order.items.map((item, idx) => {
    const optionPrice = item.selectedOption?.price || 0;
    const toppingsPrice = item.selectedToppings?.reduce((sum, t) => sum + (t.price || 0), 0) || 0;
    const basePrice = item.price - toppingsPrice - optionPrice;
    const itemTotal = item.price * item.quantity;
    
    let optionsHTML = '';
    if (item.selectedOption) {
      optionsHTML += `<div class="item-options">- ${item.selectedOption.name}${optionPrice > 0 ? ` (+${optionPrice.toLocaleString('vi-VN')}đ)` : ''}</div>`;
    }
    if (item.selectedToppings?.length > 0) {
      item.selectedToppings.forEach(t => {
        optionsHTML += `<div class="item-options">- ${t.name}${t.price > 0 ? ` (+${t.price.toLocaleString('vi-VN')}đ)` : ''}</div>`;
      });
    }
    
    return `
      <div class="item">
        <div class="item-name">${idx + 1}. ${item.name} (${basePrice.toLocaleString('vi-VN')}đ)</div>
        ${optionsHTML}
        <div class="item-qty">
          <span>x${item.quantity}</span>
          <span>${formatCurrency(itemTotal)}</span>
        </div>
      </div>
    `;
  }).join('');

  // Total section with discount and extra fee
  let totalSectionHTML = '';
  
  // Calculate subtotal (before extra fee)
  const itemsSubtotal = order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Handle extraFee - can be number or object with amount property
  const extraFeeAmount = typeof order.extraFee === 'object' ? (order.extraFee?.amount || 0) : (order.extraFee || 0);
  const extraFeeNote = typeof order.extraFee === 'object' ? order.extraFee?.description : (order.extraFeeNote || '');
  
  // Handle manual discount
  const manualDiscountAmount = order.manualDiscount?.amount || 0;
  const manualDiscountType = order.manualDiscount?.discountType || 'fixed';
  const manualDiscountValue = order.manualDiscount?.value || 0;
  
  // Check if has any discount
  const hasPromotionDiscount = order.discount && order.discount.percentage > 0;
  const hasManualDiscount = manualDiscountAmount > 0;
  const hasAnyDiscount = hasPromotionDiscount || hasManualDiscount;
  
  // Show original price if has any discount
  if (hasAnyDiscount) {
    const originalAmount = itemsSubtotal + (order.discount?.amount || 0) + manualDiscountAmount;
    totalSectionHTML += `
      <div class="total-row">
        <span>Giá gốc</span>
        <span>${formatCurrency(originalAmount)}</span>
      </div>
    `;
  }
  
  // Show promotion discount if exists
  if (hasPromotionDiscount) {
    totalSectionHTML += `
      <div class="total-row discount">
        <span>KM (${order.discount.name || order.discount.percentage + '%'})</span>
        <span>-${formatCurrency(order.discount.amount || 0)}</span>
      </div>
    `;
  }
  
  // Show manual discount if exists
  if (hasManualDiscount) {
    const discountLabel = manualDiscountType === 'percent' ? `${manualDiscountValue}%` : 'cố định';
    totalSectionHTML += `
      <div class="total-row discount">
        <span>Giảm giá (${discountLabel})</span>
        <span>-${formatCurrency(manualDiscountAmount)}</span>
      </div>
    `;
  }
  
  // Show extra fee if exists
  if (extraFeeAmount > 0) {
    // If has any discount, show subtotal first
    if (hasAnyDiscount) {
      const subtotalAfterDiscount = itemsSubtotal - (order.discount?.amount || 0) - manualDiscountAmount;
      totalSectionHTML += `
        <div class="total-row">
          <span>Tạm tính</span>
          <span>${formatCurrency(subtotalAfterDiscount)}</span>
        </div>
      `;
    }
    totalSectionHTML += `
      <div class="total-row extra-fee">
        <span>Phụ phí${extraFeeNote ? ` (${extraFeeNote})` : ''}</span>
        <span>+${formatCurrency(extraFeeAmount)}</span>
      </div>
    `;
  }

  const totalItemsCount = getTotalItemsCount(order.items);

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Hóa đơn</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        @page {
          size: ${settings.paperWidth} auto;
          margin: 0;
        }
        
        html, body { 
          width: 100%;
          height: auto;
          margin: 0;
          padding: 0;
        }
        
        body { 
          font-family: Arial, sans-serif;
          font-size: ${fs.body};
          line-height: 1.6;
          background: #fff;
          color: #000;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .receipt { 
          padding: 8px;
          width: 100%;
        }
        
        .header { 
          text-align: center; 
          margin-bottom: 8px;
        }
        
        .logo { 
          width: 50px; 
          height: 50px; 
          border-radius: 50%; 
          margin: 0 auto 0;
          display: block;
          object-fit: cover;
        }
        
        .logo-placeholder {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #16a34a;
          margin: 0 auto 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-weight: bold;
          font-size: 18px;
        }
        
        .shop-name { 
          font-size: ${fs.shopName}; 
          font-weight: bold;
          margin-bottom: 4px;
        }
        
        .shop-phone {
          font-size: ${fs.item};
          margin-bottom: 12px;
        }
        
        .divider { 
          border: none;
          border-top: 1px dashed #aaa; 
          margin: 10px 0 6px 0;
        }
        
        .title { 
          text-align: center; 
          font-weight: bold; 
          font-size: ${fs.title};
          margin-bottom: 6px;
        }
        
        .subtitle {
          text-align: center;
          font-size: ${fs.option};
          margin-bottom: 14px;
        }
        
        .info-row { 
          display: flex; 
          justify-content: space-between;
          margin-bottom: 14px;
          font-size: ${fs.item};
        }
        
        .item { 
          margin-bottom: 14px;
        }
        
        .item-name { 
          font-weight: normal;
          font-size: ${fs.item};
          margin-bottom: 4px;
        }
        
        .item-options { 
          font-size: ${parseInt(fs.option) - 1}px; 
          color: #333; 
          padding-left: 10px;
          margin-bottom: 4px;
        }
        
        .item-qty { 
          display: flex; 
          justify-content: space-between;
          font-size: ${fs.item};
          margin-top: 6px;
        }
        
        .total-section {
          margin-top: 10px;
        }
        
        .total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: ${fs.item};
        }
        
        .total-row.discount {
          font-size: ${fs.option};
        }
        
        .total-row.extra-fee {
          font-size: ${fs.option};
        }
        
        .total-row.grand-total {
          font-weight: bold;
          font-size: ${fs.total};
          border-top: 1px solid #000;
          padding-top: 0;
          margin-top: 10px;
          margin-bottom: 14px;
        }
        
        .qr-section { 
          display: flex;
          align-items: center;
          margin: 14px 0 10px 0;
          gap: 12px;
        }
        
        .qr-code { 
          width: ${settings.qrSize || 150}px; 
          height: ${settings.qrSize || 150}px;
          flex-shrink: 0;
        }
        
        .bank-info { 
          font-size: ${fs.option}; 
          text-align: left;
          flex: 1;
        }
        
        .bank-name {
          font-weight: bold;
          font-size: ${fs.option};
          margin-bottom: 3px;
        }
        
        .bank-account {
          font-size: ${fs.option};
          margin-bottom: 2px;
        }
        
        .footer { 
          text-align: center; 
          margin-top: 0; 
          font-size: ${fs.option};
        }
        
        .transfer-note {
          text-align: center;
          font-size: ${fs.option};
          margin: 10px 0 4px 0;
          padding: 0 6px 14px 6px;
          border: 1px dashed #aaa;
        }
      </style>
    </head>
    <body>
      <div class="receipt">
        <div class="header">
          ${settings.showLogo ? (shopSettings?.avatar ? `<img src="${shopSettings.avatar}" class="logo" alt="Logo">` : '<div class="logo-placeholder">GC</div>') : ''}
          <div class="shop-name">${shopSettings?.shopName || 'Green Cafe'}</div>
          <div class="shop-phone">${shopSettings?.phone || ''}</div>
        </div>
        
        <hr class="divider">
        
        <div class="title">HÓA ĐƠN BÁN HÀNG</div>
        <div class="subtitle">${orderCode} - ${new Date().toLocaleString('vi-VN')}</div>
        
        <div class="info-row">
          <span>Khách:</span>
          <span>${customerName}</span>
        </div>
        
        <hr class="divider">
        
        <div class="items">
          ${itemsHTML}
        </div>
        
        <hr class="divider">
        
        <div class="total-section">
          ${totalSectionHTML}
          <div class="total-row grand-total">
            <span>Tổng cộng (${totalItemsCount}SP)</span>
            <span>${formatCurrency(order.totalAmount)}</span>
          </div>
        </div>
        
        ${qrCodeUrl ? `
          <hr class="divider">
          <div class="qr-section">
            <img src="${qrCodeUrl}" class="qr-code" alt="VietQR">
            ${settings.showBankInfo && defaultBank ? `
            <div class="bank-info">
              <div class="bank-name">${defaultBank.bankName}</div>
              <div class="bank-account">${defaultBank.accountName}</div>
              <div class="bank-account"><b>${defaultBank.accountNumber}</b></div>
              <div class="bank-account">ND: <b>${transferContent}</b></div>
            </div>
            ` : ''}
          </div>
        ` : ''}
        
        ${settings.showTransferNote ? `
        <div class="transfer-note">
          Thanh toán tiền mặt hoặc chuyển khoản
        </div>
        ` : ''}
        
        ${settings.footerText ? `
        <div class="footer">
          ${settings.footerText}
        </div>
        ` : ''}
      </div>
    </body>
    </html>
  `;
};

export const printOrder = (order, shopSettings) => {
  const html = generatePrintHTML(order, shopSettings);
  
  // Create hidden iframe for printing
  const printFrame = document.createElement('iframe');
  printFrame.style.position = 'absolute';
  printFrame.style.top = '-10000px';
  printFrame.style.left = '-10000px';
  printFrame.style.width = '302px';
  printFrame.style.height = 'auto';
  document.body.appendChild(printFrame);

  const frameDoc = printFrame.contentWindow || printFrame.contentDocument;
  const doc = frameDoc.document || frameDoc;
  
  doc.open();
  doc.write(html);
  doc.close();

  // Wait for images to load then print
  const images = doc.querySelectorAll('img');
  let loadedImages = 0;
  const totalImages = images.length;
  let hasPrinted = false;

  const triggerPrint = () => {
    if (hasPrinted) return;
    hasPrinted = true;
    
    setTimeout(() => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      
      setTimeout(() => {
        if (document.body.contains(printFrame)) {
          document.body.removeChild(printFrame);
        }
      }, 1000);
    }, 300);
  };

  if (totalImages === 0) {
    triggerPrint();
  } else {
    images.forEach(img => {
      if (img.complete) {
        loadedImages++;
        if (loadedImages === totalImages) triggerPrint();
      } else {
        img.onload = img.onerror = () => {
          loadedImages++;
          if (loadedImages === totalImages) triggerPrint();
        };
      }
    });
    setTimeout(triggerPrint, 2000);
  }
};
